import React, { Component } from 'react';
import { Redirect, withRouter } from 'react-router';

import classNames from 'classnames';
import { Select } from 'components/ClubComponents';
import Loading from 'components/cosmos/loading';
import { Grid } from 'components/grid';
import { LegacyClubRedirectAlert } from 'components/legacyClubRedirectAlert';
import { translate } from 'components/translate';
import { ROUTE_NAMESPACES, ROUTES } from 'configs/routes/constants';
import { withShellContext } from 'hoc/withShellContext';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import throttle from 'lodash/throttle';
import { toJS } from 'mobx';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import { DatahubEventsService } from 'modules/app/services';
import {
  EVENT_ACTIONS,
  EVENTS_INTERACTIONS,
  EVENTS_CATEGORY,
  EVENTS_LABELS
} from 'modules/app/services/datahub/constants';
import DatahubService from 'modules/app/services/datahub/DatahubService';
import { buildInteractionEventData } from 'modules/app/services/datahub/helper';
import ViewContentListSchema from 'modules/app/services/datahubEvents/module/viewContentListSchema';
import { ContentService, SeasonService } from 'modules/content';
import ContentBasePage from 'modules/content/components/ContentBasePage';
import ContentImportReview from 'modules/content/components/contentImportReview';
import ContentListEmptySearch from 'modules/content/components/ContentListEmptySearch';
import ContentListSearch from 'modules/content/components/ContentListSearch';
import OnlineEventContentListEmpty from 'modules/content/components/OnlineEventContentListEmpty';
import { ContentListTabs } from 'modules/content/constants';
import ModuleEditorParamsUtils from 'modules/content/moduleEditor/ModuleEditorParamsUtils';
import { FAQ_LANGUAGES, STATIC_LINKS } from 'modules/dashboard/constants';
import { FeatureToggles } from 'modules/featureToggle';
import { MembershipEventService } from 'modules/membershipEvent';
import { EVENTS } from 'modules/membershipEvent/constants';
import BannerResendAccess from 'modules/onlineEvent/components/bannerResendAccess';
import ModalResendAccess from 'modules/onlineEvent/components/modalResendAccess';
import ModalSendLater from 'modules/onlineEvent/components/modalSendLater';
import { ResendAccessProvider } from 'modules/onlineEvent/context/resendAccess';
import PropTypes from 'prop-types';
import QueryString from 'query-string';

import ContentListEmpty from './ContentListEmpty';
import { CtaMembershipBanner } from './ctaMembershipBanner';
import { HeaderActions } from './headerActions';
import { PropagationModal, ClubLive } from './membershipEvents';
import PublishAndNotifyModal from './PublishAndNotifyModal';
import {
  contentSavedEventHandler,
  moduleSavedEventHandler
} from './resourceSavedEventHandler';
import { SortableContent } from './sortable';
import { Tabs } from './tabs';

import ContentEvents from '../events';
import CardOnboarding from '../onboarding/components/CardOnboarding';
import OnboardingService from '../onboarding/OnboardingService';
import TrackCtaModal from '../tracks/trackCtaModal';

import './ContentList.scss';

const MASS_BGS_PADDING = 24;
const ALTER_MASS_ACTION_TIMEOUT = 400;

const ADAPTER = {
  getID: (item) => item.id,
  getLabel: (item) => item.name,
  renderOption: (item) => item.name,
  renderValue: ({ value }) => {
    if (isNil(value) || isEmpty(value)) {
      return null;
    }

    return value.name;
  }
};

@translate()
@withShellContext
@inject((stores) => {
  const { hasSeason, onlineEvent, subscription, name, emailNotificationBeta } =
    stores.MembershipStore;

  return {
    hasSeason,
    onlineEvent,
    subscription,
    emailNotificationBeta,
    productName: name,
    ContentStore: stores.ContentStore,
    MembershipEventStore: stores.MembershipEventStore,
    seasonList: toJS(stores.SeasonStore.seasonList),
    UiStore: stores.UiStore,
    shortLocale: stores.UserStore.shortLocale,
    locale: stores.UserStore.locale,
    onboarding: stores.ContentOnboardingStore,
    shouldShowMembershipBanner: stores.UserStore.shouldShowMembershipBanner,
    liveMfeToggle: stores.LiveStore.mfeToggle,
    productId: stores.MembershipStore.productId,
    isCreateModuleConceptEnabled: stores.FeatureToggleStore.featureToggles.get(
      FeatureToggles.CreateModuleConcept
    )
  };
})
@observer
class ContentList extends Component {
  static propTypes = {
    ContentStore: MobxPropTypes.objectOrObservableObject,
    MembershipEventStore: MobxPropTypes.objectOrObservableObject,
    seasonList: PropTypes.array,
    UiStore: MobxPropTypes.objectOrObservableObject,
    t: PropTypes.func,
    history: PropTypes.object,
    location: PropTypes.object,
    hasSeason: PropTypes.bool,
    onlineEvent: PropTypes.bool,
    emailNotificationBeta: PropTypes.bool,
    shortLocale: PropTypes.string,
    locale: PropTypes.string,
    onboarding: MobxPropTypes.objectOrObservableObject,
    productName: PropTypes.string,
    shouldShowMembershipBanner: PropTypes.bool,
    liveMfeToggle: MobxPropTypes.objectOrObservableObject,
    isMembership: PropTypes.bool
  };

  constructor(props) {
    super(props);

    const extra = ModuleEditorParamsUtils.getExtraParam();

    this.state = {
      extraContentSelected: extra,
      tabIndex: extra ? ContentListTabs.EXTRA_MODULES : ContentListTabs.MODULES,
      isBulkUploadOpen: false,
      isPublishAndNotifyOpen: false,
      renderKey: 0
    };
  }

  language = { language: this.props.locale };

  publishAndNotifyCallback = null;

  fetchContentData() {
    const { hasSeason, ContentStore } = this.props;
    const { loaded, loadedExtra } = ContentStore;
    const { extraContentSelected } = this.state;
    const shouldFetchMainModules = !extraContentSelected && !loaded;
    const shouldFetchExtraModules = extraContentSelected && !loadedExtra;

    if (hasSeason) {
      const selectedSeason = SeasonService.getSelectedSeasonInfo();

      return ContentService.fetchModulesList(
        selectedSeason.id,
        extraContentSelected
      );
    } else if (shouldFetchMainModules || shouldFetchExtraModules) {
      return ContentService.fetchModulesList(null, extraContentSelected);
    }
  }

  async componentDidMount() {
    DatahubEventsService.sendEvent({
      schema: new ViewContentListSchema({
        arrivedFrom: window.location.href
      })
    });

    const { location } = this.props;
    const moduleId = QueryString.parse(location.search)?.moduleId;

    if (moduleId) {
      ContentService.lastEditedModule = moduleId;
    }

    await ContentService.init();
    await this.fetchContentData();
    MembershipEventService.init();

    this.listenToMakeMassActionSticky();
    await OnboardingService.init();
    moduleSavedEventHandler.register();
    contentSavedEventHandler.register();
  }

  componentDidUpdate(prevProps) {
    const { isCreateModuleConceptEnabled, isMembership } = this.props;
    const { pathname, search } = this.props.location;

    if (prevProps.location.search !== search) {
      const extra = ModuleEditorParamsUtils.getExtraParam();

      this.setState({
        tabIndex: extra
          ? ContentListTabs.EXTRA_MODULES
          : ContentListTabs.MODULES
      });
    }

    const isConceptEnabled = isCreateModuleConceptEnabled && isMembership;

    if (
      isConceptEnabled &&
      prevProps.location.pathname !== pathname &&
      pathname === ROUTE_NAMESPACES.MODULE
    ) {
      this.setState({ renderKey: this.state.renderKey + 1 });
    }
  }

  componentWillUnmount() {
    ContentService.lastCreatedModule = null;
    ContentService.lastEditedModule = null;
    moduleSavedEventHandler.unregister();
    contentSavedEventHandler.unregister();
  }

  get tabs() {
    const { t } = this.props;

    const tabs = [
      { name: t('content_list.main_content') },
      { name: t('content_list.extra_content'), track: 'Extra' },
      {
        name: t('general.tracks'),
        track: 'Track'
      }
    ];

    return tabs;
  }

  get showSeasonDropdown() {
    const { hasSeason } = this.props;
    const { seasonList } = this.props;

    return hasSeason && (seasonList || []).length > 0;
  }

  get selectedSeason() {
    return this.showSeasonDropdown
      ? SeasonService.getSelectedSeasonInfo().id
      : null;
  }

  addModule = () => {
    const { history } = this.props;
    const { extraContentSelected } = this.state;
    const { hasSeason } = this.props;

    ContentService.lastCreatedModule = null;
    ContentService.lastEditedModule = null;

    const addNewModuleUrl = hasSeason
      ? `${ROUTES.CONTENT.NEW_MODULE}?extra=${extraContentSelected}&season=${this.selectedSeason}`
      : `${ROUTES.CONTENT.NEW_MODULE}?extra=${extraContentSelected}`;

    ContentEvents.sendClick({ label: 'AddModule' });

    DatahubService.sendEvent({
      action: EVENT_ACTIONS.INTERACT,
      event: buildInteractionEventData({
        category: EVENTS_CATEGORY.CONTENT.NAME,
        label: EVENTS_LABELS.CONTENT.ADD_MODULE,
        url: '/modules',
        action: EVENTS_INTERACTIONS.CLICK
      })
    });

    history.push(addNewModuleUrl, { arrivedFrom: window.location.href });
  };

  beforeChange = (indexTab) => {
    const isExtraTab = Boolean(indexTab);

    this.updateQs(isExtraTab);

    ContentEvents.sendTabEvent(isExtraTab);
    ContentService.lastCreatedModule = null;
    ContentService.lastEditedModule = null;

    DatahubService.sendEvent({
      action: EVENT_ACTIONS.INTERACT,
      event: buildInteractionEventData({
        category: EVENTS_CATEGORY.CONTENT.CONTENT_TABS,
        label: isExtraTab
          ? EVENTS_LABELS.CONTENT.EXTRA_TAB
          : EVENTS_LABELS.CONTENT.MAIN_TAB,
        url: '/modules',
        action: EVENTS_INTERACTIONS.CLICK
      })
    });

    this.setState(
      {
        extraContentSelected: isExtraTab,
        tabIndex: indexTab
      },
      this.fetchContentData
    );
  };

  updateQs = (extra) => {
    const qs = QueryString.parse(location.search);

    qs.extra = extra;
    const stringified = QueryString.stringify(qs);

    this.props.history.replace(
      `${this.props.location.pathname}?${stringified}`
    );
  };

  handleChangeSeason = (e) => {
    const { extraContentSelected } = this.state;
    const {
      target: { value: season }
    } = e;
    const index = season.order - 1;

    SeasonService.selectedSeason = index;
    const selectedSeasonId = SeasonService.getSeasonByIndex(index).id;

    ContentService.clear();
    ContentService.fetchModulesList(selectedSeasonId, extraContentSelected);
  };

  listenToMakeMassActionSticky() {
    document.addEventListener(
      'scroll',
      throttle(this.alterMassActions, ALTER_MASS_ACTION_TIMEOUT)
    );
  }

  alterMassActions = () => {
    const massDivs = document.getElementsByClassName('content-list__options');
    const massBGs = document.getElementsByClassName(
      'content-list__options__background'
    );
    const { tabIndex } = this.state;

    if (massDivs[tabIndex] && massBGs[tabIndex]) {
      if (massDivs[1].getBoundingClientRect().bottom - MASS_BGS_PADDING < 0) {
        massDivs[tabIndex].classList.add('content-list__options_fixed');
        massBGs[tabIndex].classList.add('visible');
      } else {
        massDivs[tabIndex].classList.remove('content-list__options_fixed');
        massBGs[tabIndex].classList.remove('visible');
        window.scrollBy(0, getComputedStyle(massDivs[tabIndex]).height);
      }
    }
  };

  openPublishAndNotify = (publishAndNotifyCallback = null) => {
    this.publishAndNotifyCallback = publishAndNotifyCallback;

    this.setState({ isPublishAndNotifyOpen: true });
  };

  closePublishAndNotify = () =>
    this.setState({ isPublishAndNotifyOpen: false });

  publishAndNotify = (dontShowAgain) => {
    if (dontShowAgain) {
      MembershipEventService.dismissEvent(EVENTS.PUBLISH_AND_NOTIFY_INFO);
    }

    this.publishAndNotifyCallback && this.publishAndNotifyCallback();

    this.closePublishAndNotify();
  };

  renderHeader() {
    const {
      onlineEvent,
      UiStore: { isMobile },
      isMembership,
      t
    } = this.props;
    const showSeasonDropdown = this.showSeasonDropdown;

    const headerClasses = classNames('_align-items-center content-list-title', {
      '-online-event': onlineEvent
    });

    return (
      <>
        {!isMembership && (
          <ContentBasePage.Header
            title={t('content_list.title', { checkOnlineEvent: true })}
            isTitleLabel={false}
            className={headerClasses}
            contentClassName="_my-5 _my-lg-0 _w-full"
            hideBackHandler
          >
            {!isMobile &&
              showSeasonDropdown &&
              this.renderHeaderSeasonDropdown()}
            <HeaderActions onCreateModule={this.addModule} />
          </ContentBasePage.Header>
        )}

        <BannerResendAccess
          className="_my-5"
          events={this.props.MembershipEventStore.events || []}
        />

        {isMobile && showSeasonDropdown && this.renderHeaderSeasonDropdown()}
      </>
    );
  }

  renderHeaderSeasonDropdown() {
    const { seasonList } = this.props;
    const selectedSeason = SeasonService.getSelectedSeasonInfo();

    return (
      <div className="season-list__header__actions">
        <Select
          options={seasonList}
          value={selectedSeason}
          adapter={ADAPTER}
          style={{ width: '100%' }}
          onChange={this.handleChangeSeason}
        />
      </div>
    );
  }

  renderContentTab = () => {
    const {
      ContentStore: { loaded, search, modulesList },
      liveMfeToggle,
      shortLocale
    } = this.props;

    if (!loaded || liveMfeToggle.isLoading) {
      return <Loading center />;
    }

    if (isEmpty(modulesList)) {
      if (search) {
        return <ContentListEmptySearch className="_mt-8" />;
      }

      const linkFaq =
        STATIC_LINKS.CONTENT_MODULE[shortLocale] ||
        STATIC_LINKS.CONTENT_MODULE[FAQ_LANGUAGES.pt];

      return <ContentListEmpty linkFaq={linkFaq} />;
    }

    const {
      onboarding,
      emailNotificationBeta,
      UiStore: { isMobile },
      MembershipEventStore: { events }
    } = this.props;
    const shouldShowNotifyModal = events.includes(
      EVENTS.PUBLISH_AND_NOTIFY_INFO
    );

    return (
      <SortableContent
        ref={this.SortableContentRef}
        key={this.state.renderKey}
        type="module"
        id="module-list"
        items={modulesList}
        isMobile={isMobile}
        onboarding={onboarding}
        openPublishAndNotifyModal={this.openPublishAndNotify}
        showNotifyModal={shouldShowNotifyModal}
        emailNotificationBeta={emailNotificationBeta}
      />
    );
  };

  renderExtraContentTab = () => {
    const { shortLocale, onlineEvent, ContentStore } = this.props;
    const { modulesExtraList, loadedExtra, search } = ContentStore;

    if (!loadedExtra) {
      return <Loading center />;
    }

    if (isEmpty(modulesExtraList)) {
      if (search) {
        return <ContentListEmptySearch className="_mt-8" />;
      }

      if (onlineEvent) {
        return <OnlineEventContentListEmpty />;
      }

      const linkFaq =
        STATIC_LINKS.EXTRA_MODULE[shortLocale] ||
        STATIC_LINKS.EXTRA_MODULE[FAQ_LANGUAGES.en];

      return <ContentListEmpty linkFaq={linkFaq} />;
    }

    const {
      onboarding,
      emailNotificationBeta,
      UiStore: { isMobile },
      MembershipEventStore: { events }
    } = this.props;
    const shouldShowNotifyModal = events.includes(
      EVENTS.PUBLISH_AND_NOTIFY_INFO
    );

    return (
      <SortableContent
        type="module"
        id="module-list"
        items={modulesExtraList}
        isMobile={isMobile}
        isExtra={true}
        onboarding={onboarding}
        openPublishAndNotifyModal={this.openPublishAndNotify}
        showNotifyModal={shouldShowNotifyModal}
        emailNotificationBeta={emailNotificationBeta}
      />
    );
  };

  renderTracksTab = () => {
    return <Redirect to={ROUTES.CONTENT.TRACKS} />;
  };

  shouldRenderNewUserSection = (
    additionalContentTip,
    newUserSection,
    clubLive
  ) => newUserSection && !(additionalContentTip || clubLive);

  renderEvents(
    renderNewUserSection,
    renderClubLive,
    renderPublishAndNotifyModal
  ) {
    const { isPublishAndNotifyOpen } = this.state;
    const { locale, productName } = this.props;

    return (
      <>
        {renderNewUserSection && (
          <PropagationModal eventName={EVENTS.NEW_USER_SECTION} />
        )}
        {renderClubLive && <ClubLive locale={locale} />}
        {renderPublishAndNotifyModal && (
          <PublishAndNotifyModal
            productName={productName}
            isOpen={isPublishAndNotifyOpen}
            onCancel={this.closePublishAndNotify}
            onConfirm={this.publishAndNotify}
          />
        )}
      </>
    );
  }

  renderMembershipEvents() {
    const { emailNotificationBeta, isMembership } = this.props;
    const { loaded, modulesExtraList } = this.props.ContentStore;
    const { events } = this.props.MembershipEventStore;

    if (loaded && !isEmpty(events)) {
      const showNewUserSection = events.includes(EVENTS.NEW_USER_SECTION);
      const renderPublishAndNotifyModal =
        emailNotificationBeta &&
        events.includes(EVENTS.PUBLISH_AND_NOTIFY_INFO);
      const showAdditionalContentTip =
        events.includes(EVENTS.ADDITIONAL_CONTENT_TIP) &&
        isEmpty(modulesExtraList);
      const showClubLive =
        !isMembership &&
        events.includes(EVENTS.CLUB_LIVE) &&
        !this.isOnboardingAvailable();

      const renderNewUserSection = this.shouldRenderNewUserSection(
        showAdditionalContentTip,
        showNewUserSection,
        showClubLive
      );

      return this.renderEvents(
        renderNewUserSection,
        showClubLive,
        renderPublishAndNotifyModal
      );
    }

    return null;
  }

  isOnboardingAvailable() {
    const { onboarding } = this.props;

    return (
      onboarding.showVersionWithOnboarding && !onboarding.isProductApproved
    );
  }

  onClickTabs = (index) => {
    this.beforeChange(index);
  };

  renderTab(index) {
    return {
      [ContentListTabs.MODULES]: this.renderContentTab,
      [ContentListTabs.EXTRA_MODULES]: this.renderExtraContentTab,
      [ContentListTabs.TRACKS]: this.renderTracksTab
    }[index]?.();
  }

  renderHeaderActions() {
    const {
      UiStore: { isMobile }
    } = this.props;

    const showHeaderSeasonDropdown = !isMobile && this.showSeasonDropdown;

    return (
      <div className="_mb-5 _mb-lg-0">
        {showHeaderSeasonDropdown && this.renderHeaderSeasonDropdown()}
        <HeaderActions onCreateModule={this.addModule} />
      </div>
    );
  }

  renderTabs() {
    const {
      ContentStore: { modulesList },
      UiStore: { isMobile },
      isMembership
    } = this.props;

    if (!this.isOnboardingAvailable() || !isEmpty(modulesList)) {
      const { tabIndex } = this.state;

      return (
        <>
          <div className="_d-flex _flex-column-reverse _flex-lg-row _justify-content-lg-between">
            <Tabs
              activeIndex={tabIndex}
              items={this.tabs}
              onClick={this.onClickTabs}
              isMobile={isMobile}
            />
            {isMembership && this.renderHeaderActions()}
          </div>
          <ContentListSearch
            className="_my-5 _w-full"
            isExtra={tabIndex === ContentListTabs.EXTRA_MODULES}
          />
          {this.renderTab(tabIndex)}
        </>
      );
    }

    if (isMembership) {
      return this.renderHeaderActions();
    }
  }

  renderOnboarding() {
    if (this.isOnboardingAvailable()) {
      return <CardOnboarding />;
    }
  }

  render() {
    const { isMembership } = this.props;

    return (
      <ResendAccessProvider>
        <ContentBasePage className="content-list content__grid">
          {!isMembership && <LegacyClubRedirectAlert />}
          <Grid block noborder transparent nopadding>
            <CtaMembershipBanner />

            {this.renderHeader()}
            {this.renderTabs()}
          </Grid>

          {this.renderMembershipEvents()}
          {this.renderOnboarding()}

          <ModalResendAccess />
          <ModalSendLater />
          <ContentImportReview />
        </ContentBasePage>
        <TrackCtaModal />
      </ResendAccessProvider>
    );
  }
}

export default withRouter(ContentList);
